/**
 * SuggestionInlineEdit — Inline-Editor für den NEU-Wert einer Suggestion (T7).
 * Strings: eine Textarea. `bullets` (Array): Zeilen-Editor — ein Zeilenumbruch
 * in derselben Textarea entspricht einem Bullet (einfachste Form, ohne
 * zusätzliche Add/Remove-UI — additive MVP-Lösung).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Textarea } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'

export interface SuggestionInlineEditProps {
  value: string | string[]
  idSuffix: string
  onSave: (value: string | string[]) => void
  onCancel: () => void
}

function toText(value: string | string[]): string {
  return Array.isArray(value) ? value.join('\n') : value
}

function fromText(text: string, isBulletList: boolean): string | string[] {
  if (!isBulletList) return text
  return text.split('\n').map((line) => line.trim()).filter(Boolean)
}

export function SuggestionInlineEdit({ value, idSuffix, onSave, onCancel }: SuggestionInlineEditProps) {
  const { t } = useTranslation()
  const isBulletList = Array.isArray(value)
  const [text, setText] = useState(() => toText(value))

  return (
    <div className="text-bulk-review-inline-edit space-y-2">
      <Textarea
        id={`${EID['text-bulk-review-inline-edit-textarea']}-${idSuffix}`}
        label={t('channelContent.card.new')}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={isBulletList ? Math.max(3, value.length) : 4}
        autoFocus
      />
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        {t('productContent.channels.charsCounter', { value: text.length })}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          id={`${EID['text-bulk-review-inline-edit-save-btn']}-${idSuffix}`}
          onClick={() => onSave(fromText(text, isBulletList))}
          className="rounded-md bg-orange-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-orange-700"
        >
          {t('channelContent.card.saveEdit')}
        </button>
        <button
          type="button"
          id={`${EID['text-bulk-review-inline-edit-cancel-btn']}-${idSuffix}`}
          onClick={onCancel}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800"
        >
          {t('channelContent.card.cancelEdit')}
        </button>
      </div>
    </div>
  )
}
